import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { usePortalPosition } from '@/hooks/usePortalPosition';

import './Select.css';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type CommonProps = {
  options: SelectOption[];
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
  /** When `true`, renders an in-dropdown search input that filters `options`
   * by `label` (case-insensitive substring). Keyboard navigation operates on
   * the filtered list. Used by the catalogue zone filter (ADR-016 D8). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Optional text rendered when the search query yields no match. */
  searchEmptyLabel?: string;
};

type SingleProps = CommonProps & {
  multi?: false;
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps = CommonProps & {
  multi: true;
  value: string[];
  onChange: (value: string[]) => void;
};

export type SelectProps = SingleProps | MultiProps;

function isMulti(props: SelectProps): props is MultiProps {
  return props.multi === true;
}

export function Select(props: SelectProps) {
  const {
    options,
    placeholder,
    ariaLabel,
    disabled,
    className,
    searchable,
    searchPlaceholder,
    searchEmptyLabel,
  } = props;
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { panelStyle } = usePortalPosition({
    triggerRef: buttonRef,
    open,
    matchTriggerWidth: true,
    onTriggerOutOfView: () => {
      setOpen(false);
      setActiveIdx(-1);
      setSearchQuery('');
    },
  });
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const searchId = `${baseId}-search`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  const selectedValues = useMemo<string[]>(() => {
    if (isMulti(props)) return props.value;
    return props.value === null ? [] : [props.value];
  }, [props]);

  // Filter the options whenever the search query changes (only when
  // `searchable` is opted-in — otherwise we hand back the full list to keep
  // the legacy listbox behaviour byte-identical).
  const filteredOptions = useMemo<SelectOption[]>(() => {
    if (!searchable) return options;
    const trimmed = searchQuery.trim().toLowerCase();
    if (trimmed === '') return options;
    return options.filter((o) => o.label.toLowerCase().includes(trimmed));
  }, [options, searchable, searchQuery]);

  const buttonLabel = useMemo(() => {
    if (selectedValues.length === 0) return placeholder ?? '';
    if (isMulti(props) && selectedValues.length > 1) {
      // Show concatenated labels (the first 2 by default), with a "+N" suffix
      // for the remaining selections. The CSS rule on `.select__label`
      // (`white-space: nowrap; overflow: hidden; text-overflow: ellipsis`)
      // takes over as visual fallback when the first 2 labels themselves
      // overflow the available button width.
      const labels = selectedValues.map((v) => options.find((o) => o.value === v)?.label ?? v);
      const FIRST_VISIBLE = 2;
      if (labels.length <= FIRST_VISIBLE) return labels.join(', ');
      return `${labels.slice(0, FIRST_VISIBLE).join(', ')} +${labels.length - FIRST_VISIBLE}`;
    }
    const first = options.find((o) => o.value === selectedValues[0]);
    return first?.label ?? selectedValues[0];
  }, [options, placeholder, props, selectedValues]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIdx(-1);
    setSearchQuery('');
    buttonRef.current?.focus();
  }, []);

  // Click outside closes the listbox. We listen on the document level to
  // capture clicks anywhere; rootRef.contains lets the dropdown options
  // remain interactive without triggering a close. The portaled panel lives
  // on document.body, so we also accept clicks inside it via panelRef.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (rootRef.current.contains(target)) return;
      if (panelRef.current && panelRef.current.contains(target)) return;
      setOpen(false);
      setActiveIdx(-1);
      setSearchQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // When opening with `searchable`, focus the inner input instead of leaving
  // focus on the trigger — the keyboard flow becomes Type → Filter → ↓ to
  // the listbox.
  useEffect(() => {
    if (open && searchable) {
      // Defer to next tick so the input is mounted before focus().
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);

  // Reset the active index when the filtered list shrinks, so the highlight
  // never points to a stale index past the new bounds.
  useEffect(() => {
    if (activeIdx >= filteredOptions.length) {
      setActiveIdx(filteredOptions.length > 0 ? 0 : -1);
    }
  }, [filteredOptions.length, activeIdx]);

  const commitValue = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;
      if (isMulti(props)) {
        const next = props.value.includes(option.value)
          ? props.value.filter((v) => v !== option.value)
          : [...props.value, option.value];
        props.onChange(next);
        return;
      }
      props.onChange(option.value);
      close();
    },
    [props, close],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
        const firstEnabled = filteredOptions.findIndex((o) => !o.disabled);
        setActiveIdx(firstEnabled);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'ArrowDown': {
        event.preventDefault();
        const next = nextEnabled(filteredOptions, activeIdx, 1);
        setActiveIdx(next);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const next = nextEnabled(filteredOptions, activeIdx, -1);
        setActiveIdx(next);
        break;
      }
      case 'Home':
        event.preventDefault();
        setActiveIdx(filteredOptions.findIndex((o) => !o.disabled));
        break;
      case 'End':
        event.preventDefault();
        setActiveIdx(lastEnabled(filteredOptions));
        break;
      case 'Enter':
      case ' ': {
        // Allow space to type in the search input — only commit on Enter
        // (or a dedicated keypress like Tab) when the search field has focus.
        if (searchable && document.activeElement === searchRef.current && event.key === ' ') {
          return;
        }
        event.preventDefault();
        const opt = filteredOptions[activeIdx];
        if (opt) commitValue(opt);
        break;
      }
      case 'Tab':
        setOpen(false);
        setActiveIdx(-1);
        setSearchQuery('');
        break;
      default:
        break;
    }
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // Reset the active highlight to the first match so Enter commits on the
    // top-of-filtered-list and not on a stale index.
    const firstEnabledInFiltered = options
      .filter((o) => o.label.toLowerCase().includes(event.target.value.trim().toLowerCase()))
      .findIndex((o) => !o.disabled);
    setActiveIdx(firstEnabledInFiltered);
  };

  const isMultiMode = isMulti(props);

  return (
    <div
      ref={rootRef}
      className={['select', className, disabled ? 'select--disabled' : null]
        .filter(Boolean)
        .join(' ')}
      onKeyDown={onKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && activeIdx >= 0 ? optionId(activeIdx) : undefined}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`select__button${selectedValues.length > 0 ? ' select__button--has-value' : ''}`}
      >
        <span className="select__label">{buttonLabel || placeholder}</span>
        <span aria-hidden="true" className="select__caret">
          ▾
        </span>
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div ref={panelRef} className="select__panel" style={panelStyle}>
              {searchable ? (
                <div className="select__search">
                  <input
                    ref={searchRef}
                    id={searchId}
                    type="text"
                    className="select__search-input"
                    placeholder={searchPlaceholder ?? ''}
                    value={searchQuery}
                    onChange={onSearchChange}
                    aria-label={searchPlaceholder ?? ariaLabel}
                    aria-controls={listboxId}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              ) : null}
              <ul
                ref={listRef}
                id={listboxId}
                role="listbox"
                aria-multiselectable={isMultiMode || undefined}
                className="select__listbox"
                tabIndex={-1}
              >
                {filteredOptions.length === 0 && searchable ? (
                  <li className="select__empty" role="presentation">
                    {searchEmptyLabel ?? '—'}
                  </li>
                ) : (
                  filteredOptions.map((opt, i) => {
                    const selected = selectedValues.includes(opt.value);
                    const active = i === activeIdx;
                    return (
                      <li
                        key={opt.value}
                        id={optionId(i)}
                        role="option"
                        aria-selected={selected}
                        aria-disabled={opt.disabled || undefined}
                        className={[
                          'select__option',
                          selected ? 'select__option--selected' : null,
                          active ? 'select__option--active' : null,
                          opt.disabled ? 'select__option--disabled' : null,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => commitValue(opt)}
                      >
                        {isMultiMode ? (
                          <span aria-hidden="true" className="select__check">
                            {selected ? '✓' : ''}
                          </span>
                        ) : null}
                        <span className="select__option-label">{opt.label}</span>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function nextEnabled(options: SelectOption[], from: number, dir: 1 | -1): number {
  const n = options.length;
  if (n === 0) return -1;
  let i = from;
  for (let step = 0; step < n; step++) {
    i = (i + dir + n) % n;
    if (!options[i].disabled) return i;
  }
  return from;
}

function lastEnabled(options: SelectOption[]): number {
  for (let i = options.length - 1; i >= 0; i--) if (!options[i].disabled) return i;
  return -1;
}
